import { useCallback, useRef } from 'react';
import { useUnsavedChangesSource } from '.';

interface BlockRedirectHandlers {
  allowRedirect: () => void;
}

interface BlockRedirectOptions {
  /**
   * Retained for compatibility with existing callers. Navigation confirmation
   * never invokes a save callback; forms save only through their own controls.
   */
  onSave?: () => unknown | Promise<unknown>;
  searchParams?: readonly string[];
}

export const useBlockRedirect = (
  shouldBlock: boolean | (() => boolean),
  options: BlockRedirectOptions = {},
): BlockRedirectHandlers => {
  const shouldBlockRef = useRef(shouldBlock);
  const allowRedirectRef = useRef(false);

  shouldBlockRef.current = shouldBlock;
  const isCurrentlyBlocked =
    typeof shouldBlock === 'function' ? shouldBlock() : shouldBlock;
  if (!isCurrentlyBlocked) {
    allowRedirectRef.current = false;
  }

  useUnsavedChangesSource({
    isDirty: () => {
      const currentValue = shouldBlockRef.current;
      const dirty =
        typeof currentValue === 'function' ? currentValue() : currentValue;
      return dirty && allowRedirectRef.current === false;
    },
    searchParams: options.searchParams,
  });

  const allowRedirect = useCallback(() => {
    allowRedirectRef.current = true;
  }, []);

  return { allowRedirect };
};